import React from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  TextField,
  Paper,
  Divider,
  FormGroup,
  FormHelperText,
  Button,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import type { TimePickerProps } from '@mui/x-date-pickers/TimePicker';
import { DayOfWeek, DayAvailability, DoctorAvailability as DoctorAvailabilityType, TimeSlot } from '../../types/availability';

export type DoctorAvailability = DoctorAvailabilityType;

type TimeField = 'startTime' | 'endTime';
type SessionType = 'morning' | 'afternoon' | 'fullDay';

const daysOfWeek: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const dayLabels: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

// Helper function to convert time string to Date object
const timeStringToDate = (timeString: string): Date => {
  if (!timeString) return new Date();
  const [hours, minutes] = timeString.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

// Helper function to convert Date object to time string
const dateToTimeString = (date: Date): string => {
  return date.toTimeString().substring(0, 5);
};

interface DoctorAvailabilityFormProps {
  value: DoctorAvailability;
  onChange: (value: DoctorAvailability) => void;
  onSubmit: (value: DoctorAvailability) => void;
  isSubmitting?: boolean;
}

export const DoctorAvailabilityForm: React.FC<DoctorAvailabilityFormProps> = ({
  value,
  onChange,
  onSubmit,
  isSubmitting = false,
}) => {
  // Remove unused state variables that were shadowing props
  // Use value and onChange from props directly

  const handleToggleSameSchedule = (event: React.ChangeEvent<HTMLInputElement>) => {
    const useSameSchedule = event.target.checked;
    const newValue = { ...value, useSameSchedule };
    
    if (useSameSchedule) {
      // When enabling same schedule, copy the first day's schedule to default
      const firstDay = daysOfWeek[0];
      const firstDayAvailability = value.weeklyAvailability[firstDay] || {};
      
      // Create default day availability
      const defaultDay: DayAvailability = {
        isWorking: false,
        sessions: 1,
        morningSession: { startTime: '09:00', endTime: '13:00' },
        afternoonSession: { startTime: '14:00', endTime: '18:00' },
        fullDaySession: { startTime: '09:00', endTime: '17:00' },
      };
      
      // Merge with first day's availability
      newValue.defaultAvailability = {
        ...defaultDay,
        ...firstDayAvailability,
        morningSession: { ...defaultDay.morningSession, ...(firstDayAvailability.morningSession || {}) },
        afternoonSession: { ...defaultDay.afternoonSession, ...(firstDayAvailability.afternoonSession || {}) },
        fullDaySession: { ...defaultDay.fullDaySession, ...(firstDayAvailability.fullDaySession || {}) },
      };
    } else {
      // When disabling same schedule, ensure all days have their own schedule
      const weeklyAvailability = { ...value.weeklyAvailability };
      
      // Initialize any missing days with default values
      daysOfWeek.forEach(day => {
        if (!weeklyAvailability[day]) {
          weeklyAvailability[day] = {
            isWorking: value.defaultAvailability?.isWorking || false,
            sessions: value.defaultAvailability?.sessions || 1,
            morningSession: { 
              ...{ startTime: '09:00', endTime: '13:00' },
              ...(value.defaultAvailability?.morningSession || {}) 
            },
            afternoonSession: { 
              ...{ startTime: '14:00', endTime: '18:00' },
              ...(value.defaultAvailability?.afternoonSession || {}) 
            },
            fullDaySession: { 
              ...{ startTime: '09:00', endTime: '17:00' },
              ...(value.defaultAvailability?.fullDaySession || {}) 
            },
          };
        }
      });
      
      newValue.weeklyAvailability = weeklyAvailability;
    }
    
    onChange(newValue);
  };

  const handleDayChange = <K extends keyof DayAvailability>(
    day: DayOfWeek,
    field: K,
    fieldValue: DayAvailability[K]
  ) => {
    if (fieldValue === null) return;

    const newWeeklyAvailability = { ...value.weeklyAvailability };
    const existingDay = newWeeklyAvailability[day] || {};
    
    // Create default day availability
    const defaultDay: DayAvailability = {
      isWorking: false,
      sessions: 1,
      morningSession: { startTime: '09:00', endTime: '13:00' },
      afternoonSession: { startTime: '14:00', endTime: '18:00' },
      fullDaySession: { startTime: '09:00', endTime: '17:00' },
    };
    
    // Merge existing day with defaults
    const dayAvailability: DayAvailability = {
      ...defaultDay,
      ...existingDay,
      morningSession: { ...defaultDay.morningSession, ...(existingDay.morningSession || {}) },
      afternoonSession: { ...defaultDay.afternoonSession, ...(existingDay.afternoonSession || {}) },
      fullDaySession: { ...defaultDay.fullDaySession, ...(existingDay.fullDaySession || {}) },
    };

    // Update the specific field
    if (
      field === 'isWorking' ||
      field === 'sessions' ||
      field === 'morningSession' ||
      field === 'afternoonSession' ||
      field === 'fullDaySession'
    ) {
      dayAvailability[field] = fieldValue as any;
    }

    newWeeklyAvailability[day] = dayAvailability;

    onChange({
      ...value,
      weeklyAvailability: newWeeklyAvailability,
    });
  };

  const handleDefaultChange = <K extends keyof DayAvailability>(
    field: K,
    fieldValue: DayAvailability[K]
  ) => {
    if (fieldValue === null || !value.defaultAvailability) return;

    // Create default availability
    const defaultAvailability: DayAvailability = {
      isWorking: false,
      sessions: 1,
      morningSession: { startTime: '09:00', endTime: '13:00' },
      afternoonSession: { startTime: '14:00', endTime: '18:00' },
      fullDaySession: { startTime: '09:00', endTime: '17:00' },
    };

    // Create updated availability with the changed field
    const updatedAvailability: DayAvailability = {
      ...defaultAvailability,
      ...value.defaultAvailability,
      morningSession: { 
        ...defaultAvailability.morningSession, 
        ...(value.defaultAvailability.morningSession || {}) 
      },
      afternoonSession: { 
        ...defaultAvailability.afternoonSession, 
        ...(value.defaultAvailability.afternoonSession || {}) 
      },
      fullDaySession: { 
        ...defaultAvailability.fullDaySession, 
        ...(value.defaultAvailability.fullDaySession || {}) 
      },
      [field]: fieldValue
    };

    onChange({
      ...value,
      defaultAvailability: updatedAvailability,
    });
  };

  const SessionTimePicker: React.FC<{
    time: string;
    onChange: (time: string) => void;
    label: string;
  }> = ({ time, onChange, label }) => {
    const timeValue = React.useMemo(() => {
      try {
        return time ? new Date(`1970-01-01T${time}`) : null;
      } catch (e) {
        console.error('Invalid time format:', time);
        return null;
      }
    }, [time]);

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TimePicker
          label={label}
          value={timeValue}
          onChange={(newValue) => {
            if (!newValue) return;
            const timeString = newValue.toTimeString().substring(0, 5);
            onChange(timeString);
          }}
          slotProps={{
            textField: {
              fullWidth: true,
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  const renderDayFields = (day: DayOfWeek, dayAvailability: DayAvailability & { isWorking: boolean; sessions: 1 | 2 }) => {
    if (!dayAvailability.isWorking) return null;

    const handleTimeChange = (field: 'startTime' | 'endTime', session: SessionType) => (time: Date | null) => {
      if (!time) return;
      const timeString = time.toTimeString().substring(0, 5);

      const sessionKey = `${session}Session` as const;
      const currentSession = dayAvailability[sessionKey];

      handleDayChange(day, sessionKey, {
        ...currentSession,
        [field]: timeString,
      });
    };

    // Helper to create a date from time string
    const createDateFromTime = (timeStr: string): Date | null => {
      if (!timeStr) return null;
      try {
        return new Date(`1970-01-01T${timeStr}`);
      } catch (e) {
        console.error('Invalid time string:', timeStr);
        return null;
      }
    };

    // Handler for time picker changes
    const handleTimePickerChange = (session: SessionType, field: 'startTime' | 'endTime') => (time: string) => {
      const date = createDateFromTime(time);
      if (date) {
        handleTimeChange(field, session)(date);
      }
    };

    return (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={dayAvailability.sessions === 2}
                onChange={(e) =>
                  handleDayChange(day, 'sessions', e.target.checked ? 2 : 1)
                }
              />
            }
            label={dayAvailability.sessions === 2 ? 'Two Sessions' : 'One Session'}
          />
        </Grid>

        {dayAvailability.sessions === 1 ? (
          <Grid item xs={12} sm={6}>
            <SessionTimePicker
              label="Start Time"
              time={dayAvailability.fullDaySession.startTime}
              onChange={handleTimePickerChange('fullDay', 'startTime')}
            />
            <Box sx={{ mt: 2 }}>
              <SessionTimePicker
                label="End Time"
                time={dayAvailability.fullDaySession.endTime}
                onChange={handleTimePickerChange('fullDay', 'endTime')}
              />
            </Box>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Morning Session
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <SessionTimePicker
                  label="Morning Start"
                  time={dayAvailability.morningSession.startTime}
                  onChange={handleTimePickerChange('morning', 'startTime')}
                />
                <Box sx={{ mt: 2 }}>
                  <SessionTimePicker
                    label="Morning End"
                    time={dayAvailability.morningSession.endTime}
                    onChange={handleTimePickerChange('morning', 'endTime')}
                  />
                </Box>
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Afternoon Session
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <SessionTimePicker
                  label="Afternoon Start"
                  time={dayAvailability.afternoonSession.startTime}
                  onChange={handleTimePickerChange('afternoon', 'startTime')}
                />
                <Box sx={{ mt: 2 }}>
                  <SessionTimePicker
                    label="Afternoon End"
                    time={dayAvailability.afternoonSession.endTime}
                    onChange={handleTimePickerChange('afternoon', 'endTime')}
                  />
                </Box>
              </LocalizationProvider>
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const renderDefaultSchedule = () => {
    if (!value.defaultAvailability) return null;

    // Ensure default availability has all required fields
    const defaultAvailability: DayAvailability & { isWorking: boolean; sessions: 1 | 2 } = {
      isWorking: value.defaultAvailability.isWorking || false,
      sessions: value.defaultAvailability.sessions || 1,
      morningSession: value.defaultAvailability.morningSession || { startTime: '09:00', endTime: '13:00' },
      afternoonSession: value.defaultAvailability.afternoonSession || { startTime: '14:00', endTime: '18:00' },
      fullDaySession: value.defaultAvailability.fullDaySession || { startTime: '09:00', endTime: '17:00' },
    };

    return (
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
        <Typography variant="h6" gutterBottom>
          Default Schedule (applies to all days)
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={defaultAvailability.isWorking}
                onChange={(e) => handleDefaultChange('isWorking', e.target.checked)}
              />
            }
            label={defaultAvailability.isWorking ? 'Working Day' : 'Day Off'}
          />
        </FormGroup>

        {defaultAvailability.isWorking && renderDayFields('monday', defaultAvailability)}
      </Paper>
    );
  };

  const renderWeeklySchedule = () => {
    // Ensure all days have their required fields
    const weeklyAvailability = { ...value.weeklyAvailability };
    
    // Initialize any missing days with default values
    daysOfWeek.forEach(day => {
      if (!weeklyAvailability[day]) {
        weeklyAvailability[day] = {
          isWorking: false,
          sessions: 1 as const,
          morningSession: { startTime: '09:00', endTime: '13:00' },
          afternoonSession: { startTime: '14:00', endTime: '18:00' },
          fullDaySession: { startTime: '09:00', endTime: '17:00' },
        };
      }
    });
    
    // Update the state if any days were missing
    if (Object.keys(weeklyAvailability).length !== Object.keys(value.weeklyAvailability).length) {
      setTimeout(() => {
        onChange({
          ...value,
          weeklyAvailability
        });
      }, 0);
    }

    return (
      <Box>
        {daysOfWeek.map((day) => {
          const dayAvailability = weeklyAvailability[day];
          
          return (
            <Paper key={day} sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dayAvailability.isWorking}
                      onChange={(e) => handleDayChange(day, 'isWorking', e.target.checked)}
                    />
                  }
                  label={`${dayLabels[day]} - ${dayAvailability.isWorking ? 'Working' : 'Day Off'}`}
                />
              </FormGroup>

              {dayAvailability.isWorking && renderDayFields(day, dayAvailability)}
            </Paper>
          );
        })}
      </Box>
    );
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (onSubmit) {
      onSubmit(value);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <FormGroup sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={value.useSameSchedule}
              onChange={handleToggleSameSchedule}
            />
          }
          label="Use same schedule for all days"
        />
        <FormHelperText>
          {value.useSameSchedule
            ? 'The same schedule will be applied to all days of the week.'
            : 'Set individual schedules for each day of the week.'}
        </FormHelperText>
      </FormGroup>

      {value.useSameSchedule ? renderDefaultSchedule() : renderWeeklySchedule()}
    </Box>
  );
};

export default DoctorAvailabilityForm;
